/* eslint-disable jira/filename-case */
import React, {
	Component,
	type FC,
	type ComponentType,
	type ComponentPropsWithoutRef,
} from 'react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { UI_EVENT_TYPE } from '../utils/events.tsx';
import type { ProvidedProps } from '../utils/types.tsx';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type AnalyticsCallback = (analyticsEvent: UIAnalyticsEvent) => any;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type MappedProp = string | ((...args: any[]) => any) | undefined;

type WithUIAnalyticsEvent<Props, InjectedEvents extends {}> = FC<
	Props & {
		[K in keyof InjectedEvents]?: K extends keyof Props ? Props[K] : AnalyticsCallback;
	}
>;

/**
 * Adds analytics events to the given callbacks without altering the original behavior
 * Callback is expected to have analyticsEvent as a last argument
 * @example
 * ```tsx
 *  // will fire "select" event when onSelect is called
 *  fileWithUIAnalytics({ onSelect: 'select' })
 *
 *  // will fire "select" event with extra attributes
 *  fileWithUIAnalytics({ onSelect: (value) => ({ name: 'select', value: value}) }
 *  ```
 */
const fireWithUIAnalytics = <InjectedEvents extends ProvidedProps>(
	ConnectedProps: InjectedEvents,
) => {
	// sort the keys so we can set the deps of useMemo
	const providedKeys = Object.keys(ConnectedProps).sort();

	/**
	 * We implement a custom caching mechanism rather than memoizeOne. We got
	 * more ops/sec this way and we only need to bust the changing props cache
	 * instead of destroying all references on a single prop change.
	 */

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const cachedMappedProps: Record<string, any> = {}; /*    the actual prop value */
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const cachedConnectedProps: Record<string, any> =
		{}; /* the connected prop value (the callback) */

	const getStateFromMappedProps = (mapped: MappedProp[]) => {
		providedKeys.forEach((key, index) => {
			// firstly check the cache if the prop has changed before creating
			// any new connected props
			const prop = mapped[index];
			if (key in cachedMappedProps && cachedMappedProps[key] === prop) {
				return;
			}
			cachedMappedProps[key] = prop;
			// @ts-expect-error - TS7019 - Rest parameter 'attrs' implicitly has an 'any[]' type.
			cachedConnectedProps[key] = (...attrs) => {
				// assume that analytics event is always the last one (it should be by convention)
				const analyticsEvent = attrs[attrs.length - 1].clone();
				const connectedProp = ConnectedProps[key];

				// example: { onSelect: 'select' }
				if (typeof ConnectedProps[key] === 'string') {
					analyticsEvent
						.update({
							name: connectedProp,
							analyticsType: UI_EVENT_TYPE,
						})
						.fire();
					// example: { onSelect: (value) => ({ name: 'select', value: value}) }
				} else if (typeof connectedProp === 'function') {
					const update = connectedProp(...attrs);
					analyticsEvent
						.update({
							...update,
							analyticsType: UI_EVENT_TYPE,
						})
						.fire();
				}
				if (typeof prop === 'function') {
					prop(...attrs);
				}
			};
		});
		return cachedConnectedProps;
	};

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	return <T extends ComponentType<any>, Props = ComponentPropsWithoutRef<T>>(
		WrappedComponent: T,
	): WithUIAnalyticsEvent<Props, InjectedEvents> =>
		/* oxlint-disable typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any */
		/* oxlint-disable typescript/consistent-type-assertions */
		// eslint-disable-next-line jira/react/no-class-components
		class WithAnalytics extends Component<any> {
			/* oxlint-enable typescript/consistent-type-assertions */
			// Note: This seems to render weirdly in the chrome debugger. May need to change the string formatting to work around it
			static displayName = `WithAnalytics(${WrappedComponent.displayName || WrappedComponent.name || 'UnnamedComponent'}`;

			render() {
				const newMappedProps = getStateFromMappedProps(providedKeys.map((key) => this.props[key]));
				// @ts-expect-error TS2322: Type {} is not assignable to type
				return <WrappedComponent {...this.props} {...newMappedProps} />;
			}
		} as any; // oxlint-disable-line @typescript-eslint/no-explicit-any
	/* oxlint-enable typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any */
};

export default fireWithUIAnalytics;
